import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import ScrollToTop from "@/components/feature/ScrollToTop";
import WorkCard, { formatCount } from "@/components/feature/WorkCard";
import Seo from "@/components/feature/Seo";
import { getWork, works } from "@/mocks/works";
import { getCharacter } from "@/mocks/characters";

export default function WorkDetail() {
  const { id = "" } = useParams();
  const work = getWork(id);
  const character = work ? getCharacter(work.characterId) : undefined;

  const [liked, setLiked] = useState(false);
  const [followed, setFollowed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (!work) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="flex flex-col items-center justify-center px-4 py-28 text-center">
          <i className="ri-book-3-line text-5xl text-secondary-300" aria-hidden="true"></i>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">作品不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">该同人作品可能已被下架或链接有误。</p>
          <Link
            to="/works"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
          >
            返回作品列表
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const chapters = Array.from({ length: work.chapters }, (_, i) => ({
    n: i + 1,
    title: i === 0 ? `${work.title} 第${i + 1}话` : `第${i + 1}话`,
  }));
  const visibleChapters = expanded ? chapters : chapters.slice(0, 6);

  const related = works
    .filter((w) => w.id !== work.id)
    .sort((a, b) => {
      const aa = a.characterId === work.characterId ? 0 : 1;
      const bb = b.characterId === work.characterId ? 0 : 1;
      return aa - bb;
    })
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Seo
        title={`${work.title} - ${work.characterName}火影忍者同人作品`}
        description={`${work.summary} ${work.title}是一部${work.characterName}同人作品，共${work.chapters}话，标签：${work.tags.join("、")}。`}
        keywords={`${work.characterName}同人,${work.title},火影忍者同人作品`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: work.title,
            description: work.summary,
            inLanguage: "zh-CN",
            author: { "@type": "Organization", name: "火影同人志" },
            keywords: work.tags.join(","),
          },
        ]}
      />

      <Navbar />

      {/* Header */}
      <section className="bg-secondary-700">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-secondary-200">
            <Link to="/" className="hover:text-primary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/works" className="hover:text-primary-300">
              同人作品
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to={`/characters?c=${work.characterId}`} className="hover:text-primary-300">
              {work.characterName}同人
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-secondary-100">{work.title}</span>
          </nav>
        </div>
      </section>

      {/* Work main */}
      <section className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          {/* Cover */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 overflow-hidden rounded-lg bg-background-100">
              <div className="relative aspect-[16/10] overflow-hidden">
                <img
                  src={work.cover}
                  alt={`${work.title} - ${work.characterName}同人封面`}
                  title={`${work.title}火影忍者同人作品`}
                  className="h-full w-full object-cover object-top"
                />
                <span
                  className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-medium text-background-50 ${
                    work.status === "连载中" ? "bg-accent-500" : "bg-secondary-500"
                  }`}
                >
                  {work.status}
                </span>
              </div>

              <div className="flex flex-col gap-3 p-5">
                <button
                  type="button"
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-3 text-sm font-bold transition-colors ${
                    followed ? "bg-secondary-500 text-background-50" : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                  onClick={() => setFollowed((v) => !v)}
                >
                  <i className={followed ? "ri-star-fill" : "ri-star-line"} aria-hidden="true"></i>
                  {followed ? "已收藏" : "收藏作品"}
                </button>
                <button
                  type="button"
                  className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-5 py-3 text-sm font-bold transition-colors ${
                    liked
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-200 text-foreground-800 hover:bg-background-300"
                  }`}
                  onClick={() => setLiked((v) => !v)}
                >
                  <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true"></i>
                  {liked ? "已点赞" : `点赞 ${formatCount(work.likes + (liked ? 1 : 0))}`}
                </button>

                <dl className="mt-1 space-y-2.5 border-t border-background-200 pt-4 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-foreground-600">主角</dt>
                    <dd className="font-medium text-foreground-950">{work.characterName}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-600">章节</dt>
                    <dd className="font-medium text-foreground-950">{work.chapters} 话</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-600">阅读量</dt>
                    <dd className="font-medium text-foreground-950">{formatCount(work.views)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-foreground-600">最后更新</dt>
                    <dd className="font-medium text-foreground-950">{work.updatedAt}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          {/* Info + chapters */}
          <div className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              {work.tags.map((t) => (
                <span key={t} className="rounded-md bg-primary-500/10 px-2.5 py-1 text-xs font-medium text-primary-700">
                  #{t}
                </span>
              ))}
            </div>
            <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-4xl">{work.title}</h1>
            <p className="mt-2 flex flex-wrap items-center gap-3 text-sm text-foreground-600">
              <Link to={`/characters?c=${work.characterId}`} className="flex items-center gap-1 text-primary-700 hover:text-primary-800">
                <i className="ri-user-heart-line" aria-hidden="true"></i>
                {work.characterName}同人
              </Link>
              {character && <span>· {character.title}</span>}
            </p>

            <div className="mt-6 rounded-lg bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 md:text-lg">作品简介</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700 md:text-base">{work.summary}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                本作为粉丝同人二次创作，基于《火影忍者》原作世界观与角色衍生，与官方正传相互独立。创作者以细腻笔触续写角色们的另一种可能，为你带来全新的故事体验。
              </p>
            </div>

            {/* Chapters */}
            <div className="mt-8">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-black text-foreground-950 md:text-xl">章节列表</h2>
                <span className="text-sm text-foreground-600">共 {work.chapters} 话</span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {visibleChapters.map((c) => (
                  <button
                    key={c.n}
                    type="button"
                    className="group flex items-center justify-between gap-3 rounded-md bg-background-100 px-4 py-3 text-left text-sm transition-colors hover:bg-background-200"
                  >
                    <span className="flex items-center gap-2 text-foreground-800">
                      <span className="flex h-6 w-6 items-center justify-center rounded bg-secondary-500/10 text-xs font-bold text-secondary-700">
                        {c.n}
                      </span>
                      <span className="truncate">{c.title}</span>
                    </span>
                    <i className="ri-arrow-right-line text-foreground-400 transition-transform group-hover:translate-x-0.5" aria-hidden="true"></i>
                  </button>
                ))}
              </div>
              {chapters.length > 6 && (
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-5 py-2 text-sm font-medium text-foreground-700 hover:bg-background-100"
                >
                  {expanded ? "收起章节" : `展开全部 ${chapters.length} 话`}
                  <i className={`ri-arrow-down-s-line transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden="true"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="mx-auto w-full max-w-7xl px-4 pb-20 md:px-6">
        <h2 className="font-heading text-lg font-black text-foreground-950 md:text-xl">你可能还喜欢</h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
          {related.map((w) => (
            <WorkCard key={w.id} work={w} />
          ))}
        </div>
      </section>

      <Footer />
      <BackToTop />
    </>
  );
}